import { ExpeditionDocumentFileDto } from "@/types/api/api-types";
import { FileTypeEnum } from "./enums/fileType.enum";

export enum FileAction {
  FO_CREATE_EXPEDITION,
  FO_VALIDATE_EXPEDITION,
  FO_DETAIL_EXPEDITION,
  FO_VALIDATED_EXPEDITION,
  FO_SIDE_PANEL_EXPEDITION,
  BO_SIDE_PANEL_EXPEDITION,
  BO_FINALIZE_EXPEDITION,
  BO_FINALIZED_EXPEDITION,
  BO_DETAIL_EXPEDITION,
}

export type FileCrud = {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canTogglePrivate: boolean;
  canToggleSendViaApi: boolean;
};

// #region File default Crud rules

export const FULL: FileCrud = {
  canCreate: true,
  canRead: true,
  canUpdate: true,
  canDelete: true,
  canTogglePrivate: true,
  canToggleSendViaApi: true,
};

export const READ_ONLY: FileCrud = {
  canCreate: false,
  canRead: true,
  canUpdate: false,
  canDelete: false,
  canTogglePrivate: false,
  canToggleSendViaApi: false,
};

export const ADD_DELETE: FileCrud = {
  canCreate: true,
  canRead: true,
  canUpdate: true,
  canDelete: true,
  canTogglePrivate: false,
  canToggleSendViaApi: false,
};

export const NO_ACCESS: FileCrud = {
  canCreate: false,
  canRead: false,
  canUpdate: false,
  canDelete: false,
  canTogglePrivate: false,
  canToggleSendViaApi: false,
};

// #endregion

export type ContextRules = Record<FileTypeEnum, FileCrud>;

// #region FO rules

export const FO_CREATE_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: NO_ACCESS,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_DAE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_OTHER]: ADD_DELETE,

  // Files usualy private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_LICENCE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_OTHER]: NO_ACCESS,
  [FileTypeEnum.PRODUCT_COLA]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: NO_ACCESS,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;

export const FO_VALIDATE_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: NO_ACCESS,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_DAE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_OTHER]: ADD_DELETE,

  // Files usualy private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_LICENCE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_OTHER]: NO_ACCESS,
  [FileTypeEnum.PRODUCT_COLA]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: NO_ACCESS,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;

export const FO_DETAIL_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: NO_ACCESS,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_DAE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_OTHER]: ADD_DELETE,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Files usualy private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_LICENCE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_OTHER]: NO_ACCESS,
  [FileTypeEnum.PRODUCT_COLA]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: NO_ACCESS,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;

export const FO_VALIDATED_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: NO_ACCESS,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_DAE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: ADD_DELETE,
  [FileTypeEnum.EXPEDITION_OTHER]: ADD_DELETE,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Files usualy private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_LICENCE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_OTHER]: NO_ACCESS,
  [FileTypeEnum.PRODUCT_COLA]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: NO_ACCESS,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;

export const FO_SIDE_PANEL_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: NO_ACCESS,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_DAE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_OTHER]: READ_ONLY,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Files usualy private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_LICENCE]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: NO_ACCESS,
  [FileTypeEnum.IMPORTER_OTHER]: NO_ACCESS,
  [FileTypeEnum.PRODUCT_COLA]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: NO_ACCESS,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;
// #endregion

// #region BO rules
export const BO_FINALIZE_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: NO_ACCESS,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: FULL,
  [FileTypeEnum.EXPEDITION_DAE]: FULL,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: FULL,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: FULL,
  [FileTypeEnum.EXPEDITION_OTHER]: FULL,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Files usually private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: FULL,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: FULL,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: FULL,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: FULL,
  [FileTypeEnum.IMPORTER_LICENCE]: FULL,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: FULL,
  [FileTypeEnum.IMPORTER_OTHER]: FULL,
  [FileTypeEnum.PRODUCT_COLA]: FULL,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: FULL,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;

export const BO_FINALIZED_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE]: NO_ACCESS,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: NO_ACCESS,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: FULL,
  [FileTypeEnum.EXPEDITION_DAE]: FULL,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: FULL,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: FULL,
  [FileTypeEnum.EXPEDITION_OTHER]: FULL,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Files usually private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: FULL,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: FULL,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: FULL,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: FULL,
  [FileTypeEnum.IMPORTER_LICENCE]: FULL,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: FULL,
  [FileTypeEnum.IMPORTER_OTHER]: FULL,
  [FileTypeEnum.PRODUCT_COLA]: FULL,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: FULL,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;

export const BO_DETAIL_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_QUOTE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: READ_ONLY,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: FULL,
  [FileTypeEnum.EXPEDITION_DAE]: FULL,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: FULL,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: FULL,
  [FileTypeEnum.EXPEDITION_OTHER]: FULL,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Files usually private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: FULL,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: FULL,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: FULL,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: FULL,
  [FileTypeEnum.IMPORTER_LICENCE]: FULL,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: FULL,
  [FileTypeEnum.IMPORTER_OTHER]: FULL,
  [FileTypeEnum.PRODUCT_COLA]: FULL,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: FULL,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;

export const BO_SIDE_PANEL_EXPEDITION = {
  // Files that are always at the top, Read only
  [FileTypeEnum.EXPEDITION_INVOICE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FORM_EXCEL]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_QUOTE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_QUOTE_EXCEL]: READ_ONLY,

  // Complementary files
  [FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_DAE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_IMPORT_LICENSE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_OTHER]: READ_ONLY,

  // Other files
  [FileTypeEnum.OTHER]: NO_ACCESS,

  // Files usually private
  [FileTypeEnum.EXPEDITION_SHIPPING_LABEL]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_PRIOR_NOTICE]: READ_ONLY,
  [FileTypeEnum.IMPORTER_LICENCE]: READ_ONLY,
  [FileTypeEnum.IMPORTER_BASIC_PERMIT]: READ_ONLY,
  [FileTypeEnum.IMPORTER_OTHER]: READ_ONLY,
  [FileTypeEnum.PRODUCT_COLA]: READ_ONLY,
  [FileTypeEnum.EXPEDITION_FILES_TO_PRINT]: READ_ONLY,

  // Invoices files
  [FileTypeEnum.INVOICE_CLIENT]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN]: NO_ACCESS,
  [FileTypeEnum.INVOICE_PELICAN_EXCEL]: NO_ACCESS,

  // Deprecated files
  [FileTypeEnum.EXPEDITION_DEPRECATED_FILES]: NO_ACCESS,
} satisfies ContextRules;
// #endregion

export const FILE_CRUD_RULES = {
  [FileAction.FO_CREATE_EXPEDITION]: FO_CREATE_EXPEDITION,
  [FileAction.FO_VALIDATE_EXPEDITION]: FO_VALIDATE_EXPEDITION,
  [FileAction.FO_DETAIL_EXPEDITION]: FO_DETAIL_EXPEDITION,
  [FileAction.FO_VALIDATED_EXPEDITION]: FO_VALIDATED_EXPEDITION,
  [FileAction.FO_SIDE_PANEL_EXPEDITION]: FO_SIDE_PANEL_EXPEDITION,
  [FileAction.BO_FINALIZE_EXPEDITION]: BO_FINALIZE_EXPEDITION,
  [FileAction.BO_FINALIZED_EXPEDITION]: BO_FINALIZED_EXPEDITION,
  [FileAction.BO_DETAIL_EXPEDITION]: BO_DETAIL_EXPEDITION,
  [FileAction.BO_SIDE_PANEL_EXPEDITION]: BO_SIDE_PANEL_EXPEDITION,
} satisfies Record<FileAction, ContextRules>;

export function getVisibleFileTypes(action: FileAction): FileTypeEnum[] {
  return (Object.entries(FILE_CRUD_RULES[action]) as [FileTypeEnum, FileCrud][])
    .filter(([, crud]) => crud.canRead)
    .map(([type]) => type);
}

export function getCreatableFileTypes(action: FileAction): FileTypeEnum[] {
  return (Object.entries(FILE_CRUD_RULES[action]) as [FileTypeEnum, FileCrud][])
    .filter(([, crud]) => crud.canCreate)
    .map(([type]) => type);
}

function allFileTypes<T extends readonly FileTypeEnum[]>(
  types: T &
    (Exclude<FileTypeEnum, T[number]> extends never
      ? unknown
      : { missing: Exclude<FileTypeEnum, T[number]> }),
): T {
  return types;
}

export const PRE_EXPEDITION_TYPES = [
  FileTypeEnum.EXPEDITION_QUOTE,
  FileTypeEnum.EXPEDITION_QUOTE_EXCEL,
  FileTypeEnum.EXPEDITION_INVOICE,
  FileTypeEnum.EXPEDITION_FORM,
  FileTypeEnum.EXPEDITION_FORM_EXCEL,
  FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE,
  FileTypeEnum.EXPEDITION_OTHER,
  FileTypeEnum.OTHER,
] as const;

export const EXPEDITION_TYPES = [
  FileTypeEnum.EXPEDITION_CUSTOM_INVOICE,
  FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL,
  FileTypeEnum.EXPEDITION_DAE,
  FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE,
  FileTypeEnum.EXPEDITION_IMPORT_LICENSE,
  FileTypeEnum.IMPORTER_LICENCE,
  FileTypeEnum.IMPORTER_BASIC_PERMIT,
  FileTypeEnum.IMPORTER_OTHER,
  FileTypeEnum.PRODUCT_COLA,
] as const;

export const POST_EXPEDITION_TYPES = [
  FileTypeEnum.EXPEDITION_SHIPPING_LABEL,
  FileTypeEnum.EXPEDITION_PRIOR_NOTICE,
  FileTypeEnum.EXPEDITION_FILES_TO_PRINT,
] as const;

export const PRIORITY_TYPES = allFileTypes([
  ...PRE_EXPEDITION_TYPES,
  ...EXPEDITION_TYPES,
  ...POST_EXPEDITION_TYPES,

  // Invoices files
  FileTypeEnum.INVOICE_CLIENT,
  FileTypeEnum.INVOICE_PELICAN,
  FileTypeEnum.INVOICE_PELICAN_EXCEL,

  // Deprecated files
  FileTypeEnum.EXPEDITION_DEPRECATED_FILES,
]);

export const sortDocumentsByPriority = (
  documents: ExpeditionDocumentFileDto[],
): ExpeditionDocumentFileDto[] => {
  return [...documents].sort((a, b) => {
    const aPriority = PRIORITY_TYPES.indexOf(a.type as FileTypeEnum);
    const bPriority = PRIORITY_TYPES.indexOf(b.type as FileTypeEnum);
    const aRank = aPriority === -1 ? PRIORITY_TYPES.length : aPriority;
    const bRank = bPriority === -1 ? PRIORITY_TYPES.length : bPriority;
    if (aRank !== bRank) return aRank - bRank;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};
